import React, { useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  DateFilterModule,
  NumberFilterModule,
  PaginationModule,
  QuickFilterModule,
  RowSelectionModule,
  TextFilterModule,
  ValidationModule,
  ExternalFilterModule,
  ModuleRegistry,
  themeAlpine,
  CustomFilterModule
} from 'ag-grid-community';
import { ClientSideRowModelModule } from 'ag-grid-community';
import { formatDate, convertDbTimeIntoString } from '../../services/Helper';
import { BadgeStatus } from '../BadgeStatus/BadgeStatus';
import { BadgeEventTitle } from '../BadgeEventTitle/BadgeEventTitle';
import { EventType, ProgramOffice, User, EventStatus } from '../../services/Types';
import { BookingCategory, Event } from '../../services/Types';
import { Alert, Badge, Typography } from '@mui/material';
import { ActionMenus } from '../ActionMenus/ActionMenus';
import { useAllEvents } from '../../hooks/useAllEvents';
import { ChevronRight } from '@mui/icons-material';
import { flexItemCenter } from '../../global';
import { useEventGrid } from '../../hooks/useEventGrid';

ModuleRegistry.registerModules([
  PaginationModule,
  RowSelectionModule,
  QuickFilterModule,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ExternalFilterModule,
  CustomFilterModule,
  ValidationModule /* Development Only */,
]);

interface EventGridBookingEditorProps {
  searchCriteria: Date;
  setSelectedEvent: (event: Event) => void;
}

export const EventGridBookingEditor: React.FC<EventGridBookingEditorProps> = ({ searchCriteria, setSelectedEvent }) => {
  const gridStyle = useMemo(() => ({ height: "calc(100% - 60px)", width: "100%" }), []);

  const columnDefs: ColDef<Event>[] = [
    { field: 'id' as keyof Event,
      headerName: 'ID',
      hide: true,
      cellDataType: "number",
    },
    { field: 'title',
      headerName: 'Event',
      cellRenderer: (params: any) => {
        const titleDate = titleCellRenderer(params);
        return (
          <div style={{ display: 'flex', alignItems: 'left', gap: '5px', flexDirection: 'column', paddingTop: '10px' }}>
            <Typography variant="body2" style={{ lineHeight: '1em' }}>
                {titleDate.type}: {titleDate.date}
            </Typography>
            <BadgeEventTitle eventType={params.data.eventType} eventTitle={params.data.title} />
          </div>
        );
      },
    },
    { field: 'roomBooking',
      headerName: 'Date',
      filter: 'agTextColumnFilter',
      valueGetter: (params: any) => {
        return dateValueGetter(params);
      },
      hide: true,
    },
    { maxWidth: 50,
      cellRenderer: (params: any) => {
        return (
          <div style={flexItemCenter}>
            <ChevronRight />
          </div>
        );
      }
    }
  ];

  // Concats all the dates into a single string for the date filter
  const dateValueGetter = (params: any) => {
    const liveDate = formatDate(params.data?.roomBooking?.[0]?.date, 'date') as string;
    const dryRunDate = params.data?.roomBooking?.[1] ? formatDate(params.data?.roomBooking?.[1]?.date, 'date') as string : '';
    const publicationDate = params.data?.publicationDate ? formatDate(params.data?.publicationDate, 'date') as string : '';
    return [liveDate, dryRunDate, publicationDate].filter(Boolean).join(' ');
  };

  // Formats the search criteria date for the date filter
  const getFormattedSearchDate = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Formats the dates for the title cell
  const titleCellRenderer = (params: any) => {
    const formattedDate = getFormattedSearchDate(searchCriteria);

    const liveDate = formatDate(params.data?.roomBooking?.[0]?.date, 'date') as string;
    const dryRunDate = params.data?.roomBooking?.[1] ? formatDate(params.data?.roomBooking?.[1]?.date, 'date') as string : '';
    const publicationDate = params.data?.publicationDate ? formatDate(params.data?.publicationDate, 'date') as string : '';

    if (liveDate?.includes(formattedDate)) return {type: 'Live', date: liveDate};
    if (dryRunDate?.includes(formattedDate)) return {type: 'Dry Run', date: dryRunDate};
    if (publicationDate?.includes(formattedDate)) return {type: 'Publication', date: publicationDate};
    return {type: 'TBD', date: ''};
  };


  const { gridRef, selectedEvent, renderGrid } = useEventGrid({ columnDefs, gridStyle });

  // Filter on the searchCriteria value
  useEffect(() => {
    // Format the date to match the format in the grid (MM/DD/YYYY)
    const formattedDate = getFormattedSearchDate(searchCriteria);
    // give time for the grid to load, then run filter
    setTimeout(() => {
      gridRef?.current?.api?.setFilterModel({
        'roomBooking': {
          filterType: 'text',
          type: 'contains',
          filter: formattedDate,
        }
      });
    }, 300);
  }, [searchCriteria]);

  useEffect(() => {
    if (selectedEvent) {
      setSelectedEvent(selectedEvent);
    }
  }, [selectedEvent]);

  return renderGrid();
}; 